import React from "react";
import {
  TransformedProduct,
  EnrichedAttributeTermTransformed,
} from "../../../../types/productApiTypes";

// --- Local Types ---
interface InteractiveAttributeTermDisplayProps {
  term: EnrichedAttributeTermTransformed;
  isSelected: boolean;
  onClick: () => void;
}

interface StaticAttributeTermDisplayProps {
  term: EnrichedAttributeTermTransformed;
}

interface ProductAttributesProps {
  product: TransformedProduct;
  selectedAttributes: { [key: string]: string };
  onAttributeSelect: (attributeTaxonomy: string, termSlug: string) => void;
}

// --- Local Sub-Components ---

/**
 * Renders a single, CLICKABLE attribute term for variable products.
 */
const InteractiveAttributeTermDisplay: React.FC<
  InteractiveAttributeTermDisplayProps
> = ({ term, isSelected, onClick }) => {
  const className = `product-info-details__term product-info-details__term--${
    term.type
  } ${isSelected ? "is-active" : ""}`;

  switch (term.type) {
    case "button-color":
      return (
        <button
          className={className}
          style={{ backgroundColor: term.buttonColor }}
          onClick={onClick}
          aria-label={term.name}
          aria-pressed={isSelected}
          title={term.name}
        />
      );
    case "button-image":
      return (
        <button
          className={className}
          onClick={onClick}
          aria-label={term.name}
          aria-pressed={isSelected}
          title={term.name}
        >
          {term.buttonImage && (
            <img src={term.buttonImage.thumbnail} alt={term.name} />
          )}
        </button>
      );
    case "button-text":
    default:
      return (
        <button
          className={className}
          onClick={onClick}
          aria-pressed={isSelected}
        >
          {term.name}
        </button>
      );
  }
};

/**
 * Renders a single, STATIC attribute term for simple/grouped products.
 */
const StaticAttributeTermDisplay: React.FC<StaticAttributeTermDisplayProps> = ({
  term,
}) => {
  const className = `product-info-details__term product-info-details__term--${term.type} is-static`;

  switch (term.type) {
    case "button-color":
      return (
        <span
          className={className}
          style={{ backgroundColor: term.buttonColor }}
          title={term.name}
          aria-label={term.name}
        />
      );
    case "button-image":
      return (
        <span className={className} title={term.name} aria-label={term.name}>
          {term.buttonImage && (
            <img src={term.buttonImage.thumbnail} alt={term.name} />
          )}
        </span>
      );
    case "button-text":
    default:
      return <span className={className}>{term.name}</span>;
  }
};

// --- Main Component ---

/**
 * Renders the product's attributes.
 * It provides interactive controls for variable products and a static display for others.
 */
const ProductAttributes: React.FC<ProductAttributesProps> = ({
  product,
  selectedAttributes,
  onAttributeSelect,
}) => {
  // If there are no attributes at all, render nothing.
  if (!product.enrichedAttributes || product.enrichedAttributes.length === 0) {
    return null;
  }

  // --- Render logic for VARIABLE products ---
  if (product.isVariable) {
    // Only show attributes that are used for variations.
    const variationAttributes = product.enrichedAttributes.filter(
      (attr) => attr.hasVariations
    );

    if (variationAttributes.length === 0) {
      return null;
    }

    return (
      <div className="product-info-details__attributes">
        {variationAttributes.map((attribute) => (
          <div
            key={attribute.id}
            className="product-info-details__attribute-group"
          >
            <h4 className="product-info-details__attribute-group-title">
              {attribute.name}
            </h4>
            <div className="product-info-details__attribute-terms">
              {attribute.terms.map((term) => {
                const isSelected =
                  selectedAttributes[attribute.taxonomy] === term.slug;
                return (
                  <InteractiveAttributeTermDisplay
                    key={term.id}
                    term={term}
                    isSelected={isSelected}
                    onClick={() =>
                      onAttributeSelect(attribute.taxonomy, term.slug)
                    }
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // --- Render logic for SIMPLE, GROUPED, EXTERNAL products ---
  // Only show attributes that are NOT used for variations.
  const displayAttributes = product.enrichedAttributes.filter(
    (attr) => !attr.hasVariations
  );

  if (displayAttributes.length === 0) {
    return null;
  }

  return (
    <div className="product-info-details__attributes product-info-details__attributes--static">
      {displayAttributes.map((attribute) => (
        <div
          key={attribute.id}
          className="product-info-details__attribute-group"
        >
          <h4 className="product-info-details__attribute-group-title">
            {attribute.name}:
          </h4>
          <div className="product-info-details__attribute-terms">
            {attribute.terms.map((term) => (
              <StaticAttributeTermDisplay key={term.id} term={term} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductAttributes;
